'use client';

import type { ReactNode } from 'react';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MuiToggleButton from '@mui/material/ToggleButton';
import { Stack, Typography } from '@/primitives';
import BreedplanInterpretationInfoTrigger from '@/components/publicSearch/animal/ebv/BreedplanInterpretationInfoTrigger';
import { MATING_DISPLAY_BY } from '../types';
import type { MatingDisplayBy } from '../types';

interface MatingResultsToolbarProps {
  displayBy: MatingDisplayBy;
  onDisplayByChange: (value: MatingDisplayBy) => void;
  showBreedplanHelp: boolean;
  /** Right-aligned action(s), e.g. the CSV export button. */
  exportButton: ReactNode;
}

/** Top bar: "Group by" Sire/Dam toggle (left), BREEDPLAN help + export action (right). */
export default function MatingResultsToolbar({
  displayBy,
  onDisplayByChange,
  showBreedplanHelp,
  exportButton,
}: MatingResultsToolbarProps) {
  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        mb: 2,
        gap: 1,
        flexShrink: 0,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: { xs: 0.75, md: 1 },
          minWidth: 0,
          flex: '1 1 auto',
        }}
      >
        <Typography
          variant="bodymedium"
          sx={{
            fontSize: { xs: '0.8125rem', md: '0.875rem' },
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Group by
        </Typography>
        <ToggleButtonGroup
          aria-label="Group mating results by"
          value={displayBy}
          exclusive
          onChange={(_, newValue) => {
            if (newValue) onDisplayByChange(newValue as MatingDisplayBy);
          }}
          size="small"
          sx={{ flexShrink: 0, height: { xs: 32, md: 36 } }}
        >
          <MuiToggleButton
            value={MATING_DISPLAY_BY.SIRE}
            sx={{ height: { xs: 32, md: 36 } }}
          >
            Sire
          </MuiToggleButton>
          <MuiToggleButton
            value={MATING_DISPLAY_BY.DAM}
            sx={{ height: { xs: 32, md: 36 } }}
          >
            Dam
          </MuiToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <BreedplanInterpretationInfoTrigger
          show={showBreedplanHelp}
          dataDdActionName="Mating Predictor: BREEDPLAN Interpretation Info"
        />
        {exportButton}
      </Stack>
    </Stack>
  );
}
